import { useEffect, useState } from 'react'

import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { FormCloseCircle, UserForm } from 'entities/close-circle/ui/user-form'
import { DOCUMENT } from 'entities/user/module'

import { CloseCircleApi } from 'shared/api/close-circle'
import error_icon from 'shared/images/error.png'
import success_icon from 'shared/images/success_popup.png'
import citizenshipStore from 'shared/store/citizenship-store'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import { useDialogBottomSheet } from '../../shared/ui/dialog-bottom-sheet/lib'

import s from './styles.module.scss'

export enum RESULT_REQUEST {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export const CloseCircleEdit = observer(() => {
  const { id: userId } = useParams()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const { t } = useTranslation()

  const [loading, setLoading] = useState<boolean>(false)

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormCloseCircle>()

  const navigate = useNavigate()

  const closePopUp = (isSuccess?: boolean) => {
    if (isSuccess) {
      navigate(
        RoutingPaths.closeCircle.detailCloseCircle.path(String(userId)),
        {
          replace: true,
        },
      )
    }

    closeDialog()
  }

  const citizenshipsData = citizenshipStore?.citizenshipAvia?.map(
    (citizenship) => ({
      value: citizenship?.code,
      name: citizenship?.country,
    }),
  )

  const typesDocuments = [
    { name: t('id_passport'), value: DOCUMENT.ID },
    { name: t('passport'), value: DOCUMENT.PASSPORT },
    { name: t('birth_certificate'), value: DOCUMENT.BIRTH_CERTIFICATE },
  ]

  const onSubmit = async (data: FormCloseCircle) => {
    try {
      setLoading(true)

      await CloseCircleApi.updateUserDocuments(data?.form, userId!)

      showPopUp(RESULT_REQUEST.SUCCESS)
    } catch (error: unknown) {
      showPopUp(RESULT_REQUEST.ERROR)
    } finally {
      setLoading(false)
    }
  }

  const showPopUp = (result: RESULT_REQUEST) => {
    openDialog({
      child: (
        <PopupContent
          image={result === RESULT_REQUEST.SUCCESS ? success_icon : error_icon}
          title={
            result === RESULT_REQUEST.SUCCESS
              ? t('success_title')
              : t('error_popup')
          }
          description={
            result === RESULT_REQUEST.SUCCESS
              ? t('success_edited')
              : t('error_popup_description')
          }
          button={t('apply')}
          onClick={() => closePopUp(result === RESULT_REQUEST.SUCCESS)}
        />
      ),
      isDialog: false,
    })
  }

  const getCloseCircle = async (id: string) => {
    try {
      const { data: closeCircle } =
        await CloseCircleApi.getUserDocumentsById(id)

      setValue('form', {
        ...closeCircle?.data,
        hasMiddle: !!closeCircle?.data?.middle_name,
      })
    } catch {
      openDialog({
        child: (
          <PopupContent
            button={t('close')}
            title={t('error')}
            description={t('close_circle.error_user_not_allowed')}
            onClick={() => navigate(-1)}
          />
        ),
        isDialog: false,
      })
    }
  }

  useEffect(() => {
    if (userId) getCloseCircle(userId)
  }, [])

  const formData = useWatch({ control, name: 'form' })

  return (
    <div className={s.wrapper}>
      <HeaderGoBack
        title={t('edit_user')}
        buttonWhite
      />

      {loading || !formData ? (
        <Loader marginTop={200} />
      ) : (
        <form
          className={s.form}
          onSubmit={handleSubmit(onSubmit)}
        >
          <UserForm
            setValue={setValue}
            register={register}
            control={control}
            errors={errors}
            options={{
              showLabel: true,
              citizenships: citizenshipsData,
              typesDocuments: typesDocuments,
            }}
          />

          <ButtonContinue type="submit">
            <span className={s.text}>{t('save')}</span>
          </ButtonContinue>
        </form>
      )}
    </div>
  )
})
